import { isAxiosError } from 'axios';
import { apiClient } from '@shared/api';
import type { ApiResponse } from '@shared/types';
import type {
  GetNotificationsParams,
  NotificationActionResult,
  NotificationCounts,
  NotificationPage,
} from '../types/notification.types';

/**
 * The outcome of a recovery verb (N5 · 86d3w2kg2).
 *
 * ⚠️ These three calls deliberately do NOT share the generic throw-on-`!success` unwrap, and that is
 * the whole point of this type. N4 refuses a stale action with an HTTP 400 whose BODY carries the
 * notification projected from live journey facts — the corrected row. A blanket "throw on !success"
 * would discard `data` on exactly that path, leaving the client rendering the same stale row that
 * produced the wrong button, so the user presses it again and the loop never breaks.
 */
export interface RecoveryResponse {
  /** True on 200, false on the 400-with-body refusal. */
  ok: boolean;
  /** The server's row + counts. Present on BOTH branches — that is N4's contract. */
  result: NotificationActionResult;
  /** The human explanation, on the refusal branch. */
  message?: string;
}

/**
 * N7 — the list endpoint refused the cursor we sent.
 *
 * ⚠️ A DISTINCT TYPE ON PURPOSE, and the reason is a deploy-day failure mode rather than a niceness.
 * `NotificationCursor` carries a version tag (`v1|micros|id`); the day that tag is bumped, every
 * client mid-scroll holds a cursor the new server will not decode, and the repository answers with a
 * `BusinessRuleException` → HTTP 400. Left as a generic `Error`, the house service pattern turns that
 * into "Could not load more notifications" in a red banner — for every scrolled user at once, on a
 * deploy where nothing is actually wrong. Typed, the store can tell "your bookmark expired" apart
 * from "the server is unwell" and quietly start again from page 1.
 */
export class StaleCursorError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StaleCursorError';
  }
}

class NotificationService {
  // ── Read ───────────────────────────────────────────────────────────────────

  async getPage(params: GetNotificationsParams = {}): Promise<NotificationPage> {
    try {
      const { data } = await apiClient.get<ApiResponse<NotificationPage>>('/notifications', {
        params,
      });
      if (data.success && data.data) return data.data;
      throw new Error(data.message || 'Failed to load notifications');
    } catch (err) {
      // ⚠️ Narrowed to a request that actually CARRIED a cursor. A 400 on the unpaged first page is a
      // different animal — a malformed request, say — and translating it here would tell the store to
      // "reset to page 1 and try again" on the very call that just failed, which is an infinite loop
      // dressed as error handling.
      if (params.cursor && isAxiosError(err) && err.response?.status === 400) {
        throw new StaleCursorError('Pagination cursor is no longer valid');
      }
      throw err;
    }
  }

  /**
   * Whole-inbox totals, on their own. Separate from the page because the sidebar badge renders at app
   * boot, long before any popover is opened — folding it into the page envelope would drag 50 rows of
   * timeline JSON along with every badge refresh.
   */
  async getCounts(): Promise<NotificationCounts> {
    const { data } = await apiClient.get<ApiResponse<NotificationCounts>>('/notifications/counts');
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load notification counts');
  }

  // ── Mutations ──────────────────────────────────────────────────────────────
  //
  // Each returns fresh server-authoritative counts. ASSIGN them; never recompute a badge from the
  // loaded page — a page is a window and a badge is a total.

  async markRead(ids: number[]): Promise<NotificationCounts> {
    return this.postCounts('/notifications/mark-read', { ids });
  }

  /**
   * "Mark all read" — the SAME endpoint, with the all-mode wire shape (`ids` null).
   *
   * There is no separate `/mark-all-read` route, and the client must not improvise one by posting
   * every id it happens to have loaded: a loaded window is a subset, and "all" has to reach rows the
   * client has never seen. The server also skips needs-action rows on this path, so clearing the
   * badge can never discharge a recovery obligation.
   */
  async markAllRead(): Promise<NotificationCounts> {
    return this.postCounts('/notifications/mark-read', { ids: null });
  }

  async markUnread(ids: number[]): Promise<NotificationCounts> {
    return this.postCounts('/notifications/mark-unread', { ids });
  }

  async markDone(id: number): Promise<NotificationCounts> {
    return this.postCounts(`/notifications/${id}/done`, {});
  }

  async remove(id: number): Promise<NotificationCounts> {
    const { data } = await apiClient.delete<ApiResponse<NotificationCounts>>(`/notifications/${id}`);
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to delete notification');
  }

  // ── Recovery ───────────────────────────────────────────────────────────────

  retry(id: number): Promise<RecoveryResponse> {
    return this.recover(id, 'retry');
  }

  cancel(id: number): Promise<RecoveryResponse> {
    return this.recover(id, 'cancel');
  }

  /**
   * The N4 recovery verb for a row whose record may already exist in NetSuite.
   *
   * ⛔ Not to be confused with the store's `resync()`, which is a read-only whole-feed merge on
   * reconnect. Two different jobs; one name between them would be a bug waiting to happen.
   */
  reconcile(id: number): Promise<RecoveryResponse> {
    return this.recover(id, 'reconcile');
  }

  private async postCounts(url: string, body: unknown): Promise<NotificationCounts> {
    const { data } = await apiClient.post<ApiResponse<NotificationCounts>>(url, body);
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Notification update failed');
  }

  /**
   * ⚠️ A 400 here is DATA, not an error.
   *
   * Axios rejects on any non-2xx, so the refusal arrives as a thrown `AxiosError` — that is exactly
   * where a normal service would lose the body. It is caught, and a 400 carrying `data` is returned
   * as `{ ok: false, result, message }` so the caller can re-render the row from the server's
   * corrected copy.
   *
   * Everything else — a network failure, a 403, a 404, a 500 — RETHROWS, because those are the only
   * cases where the caller should roll back. Distinguishing them matters: rolling back on a refusal
   * would restore the stale row the server just corrected.
   */
  private async recover(id: number, verb: 'retry' | 'cancel' | 'reconcile'): Promise<RecoveryResponse> {
    try {
      const { data } = await apiClient.post<ApiResponse<NotificationActionResult>>(
        `/notifications/${id}/${verb}`,
        {},
      );

      if (data.success && data.data) return { ok: true, result: data.data };
      throw new Error(data.message || 'Recovery failed');
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 400) {
        const body = err.response.data as ApiResponse<NotificationActionResult> | undefined;

        // The 400-with-body contract. Guarded rather than assumed: a 400 from anywhere else in the
        // stack (a model-binding failure, a proxy) has no `data`, and treating that as a refusal
        // would hand the store an undefined row to splice in.
        if (body?.data) {
          return { ok: false, result: body.data, message: body.message };
        }
      }

      throw err;
    }
  }
}

export const notificationService = new NotificationService();
